import { css } from '@emotion/react';
import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { Box } from '../../Box';
import {
  type IconSize,
  type IconVariantTypes,
  Icon,
  IconSizeMap,
} from '../../Icon';
import { type TTextSize, Text } from '../../Typography';

export type FeatureListItemProps = {
  /**
   * Icon variant to display
   */
  iconVariant?: IconVariantTypes;
  /**
   * Size of the icon
   */
  iconSize?: IconSize;
  /**
   * Color of the icon
   */
  iconColor?: string;
  /**
   * Main label text
   */
  label: React.ReactNode;
  /**
   * Size of the label text
   */
  labelSize?: TTextSize;
  /**
   * Optional subtitle/description text
   */
  subtitle?: React.ReactNode;
  /**
   * Loading state - shows skeleton when true
   */
  isLoading?: boolean;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Whether the feature list item should be displayed inline
   */
  isInline?: boolean;
};

const FeatureListItem = ({
  iconVariant,
  label,
  labelSize = 'md',
  subtitle,
  iconSize = 'large',
  iconColor,
  isLoading = false,
  className,
  isInline = false,
  ...rest
}: FeatureListItemProps) => {
  const iconDimensions = IconSizeMap[iconSize];

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" color="var(--text-primary)">
        <Box
          display="flex"
          alignItems="center"
          gap="var(--spacing-2)"
          maxHeight={iconDimensions.height}
          className={className}
          {...rest}
        >
          <Skeleton
            circle
            width={iconDimensions.width}
            height={iconDimensions.height}
            borderRadius={4}
          />
          <Skeleton width={120} height={16} borderRadius={4} />
        </Box>

        {subtitle && (
          <Box display="flex" alignItems="center" gap="var(--spacing-3)">
            <Box minWidth="var(--spacing-6)">&nbsp;</Box>
            <Skeleton width={150} height={14} borderRadius={4} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" color="var(--text-primary)">
      {isInline ? (
        <Box display="flex" alignItems="center" gap="var(--spacing-1)">
          <Text as="span">
            <Text as="span" fontWeight="bold">
              {label}{' '}
            </Text>
            {subtitle}
          </Text>
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            gap="var(--spacing-2)"
            className={className}
            {...rest}
          >
            {iconVariant && (
              <Icon
                variant={iconVariant}
                size={iconSize}
                fill={iconColor}
                css={css`
                  flex-shrink: 0;
                `}
              />
            )}

            <Text fontWeight="semibold" size={labelSize}>
              {label}
            </Text>
          </Box>

          {subtitle && (
            <Box display="flex" alignItems="center" gap="var(--spacing-3)">
              <Box minWidth="var(--spacing-6)">&nbsp;</Box>
              <Text size="sm">{subtitle}</Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default FeatureListItem;
